/** Ground-floor acceptance probe using the game's actual collider field.
 * The flat authoring parcel meets the sill. Upper floors keep their existing
 * stair integration; this bounded probe deliberately does not certify them. */
import { BoxColliderField } from '../worldgen/boxColliders.js';
import type { BuildingProgram } from './program.js';
export const PROGRAM_PROBE_RADIUS=10000;
export const BUILDING_AGENT={radius:.45,height:1.8};
export function programColliderField(program:BuildingProgram){
  const field=new BoxColliderField(PROGRAM_PROBE_RADIUS);
  for(const b of [...program.plan.collision,...program.interior?.collision??[]])field.addBox({
    base:[b.x,PROGRAM_PROBE_RADIUS+b.y,b.z],up:[0,1,0],width:[1,0,0],
    halfWidth:b.w/2,height:b.h,halfDepth:b.d/2,standable:b.wall!==true,
  });
  return field;
}
export function programAgentFits(field:BoxColliderField,x:number,floor:number,z:number){
  const {radius,height}=BUILDING_AGENT,out={x:0,y:0,z:0};
  // Bottom/top sphere checks cover the full upright body; resolve is also
  // exercised so the preview and test use the same lateral push as gameplay.
  return !field.resolve(x,PROGRAM_PROBE_RADIUS+floor,z,radius,out)
    && !field.occludes(x,PROGRAM_PROBE_RADIUS+floor+radius+.001,z,radius)
    && !field.occludes(x,PROGRAM_PROBE_RADIUS+floor+height-radius,z,radius);
}
export interface ProgramAccessReport { passed:boolean; visited:number; routes:{space:string;points:[number,number,number][]}[]; errors:string[]; unchecked:string[] }
export function validateProgramAccess(program:BuildingProgram):ProgramAccessReport {
  const errors:string[]=[],unchecked=program.spaces.filter(s=>s.level>0).map(s=>s.id),routes:ProgramAccessReport['routes']=[];
  const entrance=program.portals.find(p=>p.from==='outside'),ground=program.spaces.filter(s=>s.level===0);
  if(!entrance||!ground.length)return {passed:false,visited:0,routes,errors:['No playable ground-floor entrance'],unchecked};
  const field=programColliderField(program),floor=entrance.position[1],step=.4;
  for(const p of program.portals.filter(p=>p.capability==='walk')){
    if(p.width<BUILDING_AGENT.radius*2||p.height<BUILDING_AGENT.height)errors.push(`${p.id}: insufficient opening`);
    for(let i=-6;i<=6;i++)if(!programAgentFits(field,p.position[0]+p.normal[0]*i*.15,floor,p.position[2]+p.normal[2]*i*.15)){
      errors.push(`${p.id}: collider blocks the doorway clearance`);break;
    }
  }
  const sx=entrance.position[0]+entrance.normal[0]*1.6,sz=entrance.position[2]+entrance.normal[2]*1.6;
  const key=(x:number,z:number)=>`${x},${z}`,queue:[[number,number]]=[[0,0]],parents=new Map<string,string|null>([['0,0',null]]),positions=new Map<string,[number,number]>([['0,0',[0,0]]]);
  if(!programAgentFits(field,sx,floor,sz))errors.push('Entrance approach blocked');
  const cells=ground.flatMap(s=>s.cells),minX=Math.min(...cells.map(c=>c.x))-4,maxX=Math.max(...cells.map(c=>c.x))+4,minZ=Math.min(...cells.map(c=>c.z))-4,maxZ=Math.max(...cells.map(c=>c.z))+4;
  for(let head=0;head<queue.length&&head<10000;head++){
    const [x,z]=queue[head];
    for(const [dx,dz] of [[1,0],[-1,0],[0,1],[0,-1]]){
      const nx=x+dx,nz=z+dz,k=key(nx,nz),wx=sx+nx*step,wz=sz+nz*step;
      if(parents.has(k)||wx<minX||wx>maxX||wz<minZ||wz>maxZ)continue;
      if(!programAgentFits(field,wx,floor,wz)||!programAgentFits(field,wx-dx*step/2,floor,wz-dz*step/2))continue;
      parents.set(k,key(x,z));positions.set(k,[nx,nz]);queue.push([nx,nz]);
    }
  }
  if(queue.length>=10000)errors.push('Access search budget exceeded');
  for(const space of ground){
    const hit=queue.find(([x,z])=>space.cells.some(c=>Math.abs(sx+x*step-c.x)<.7&&Math.abs(sz+z*step-c.z)<.7));
    if(!hit){errors.push(`${space.id}: no physical route from the entrance`);continue;}
    const points:[number,number,number][]=[];let k:string|null=key(...hit);
    while(k!==null){const [x,z]=positions.get(k)!;points.push([sx+x*step,floor,sz+z*step]);k=parents.get(k)!;}
    routes.push({space:space.id,points:points.reverse()});
  }
  return {passed:errors.length===0,visited:queue.length,routes,errors,unchecked};
}
